import React, { useState, useEffect } from 'react';
import Button from '../components/Button';
import Card from '../components/Card';
import NavBar from '../components/NavBar';
import ProgressBar from '../components/ProgressBar';
import Badge from '../components/Badge';
import LoadingSpinner from '../components/LoadingSpinner';
import EducationalBanner from '../components/EducationalBanner';
import { useFirstVisit } from '../hooks/useFirstVisit';

function FoodScan() {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedFood, setScannedFood] = useState(null);
  const [todayScore] = useState(85);
  const { isFirstVisit, setVisited } = useFirstVisit('nutrition');
  const [showEducationalBanner, setShowEducationalBanner] = useState(false);

  // Scroll to top on mount and check for first visit
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (isFirstVisit) {
      setShowEducationalBanner(true);
    }
  }, [isFirstVisit]);

  function simulatePhotoUpload() {
    setIsScanning(true);
    // Simulate scanning delay
    setTimeout(() => {
      setScannedFood({
        name: "Grilled Chicken Salad",
        protein: "28g",
        vegetables: "2 cups",
        carbs: "15g",
        calories: "320",
        score: 85
      });
      setIsScanning(false);
    }, 1500);
  }

  // Handle closing the educational banner
  const handleCloseBanner = () => {
    setShowEducationalBanner(false);
    setVisited();
  };

  return (
    <div style={{ maxWidth: '480px', margin: 'auto', padding: '20px', backgroundColor: '#f5f7fa', minHeight: '100vh', paddingTop: '60px' }}>
      <NavBar title="📸 FoodScan Tracker" />
      
      {/* Educational Banner - Only shows on first visit */}
      {showEducationalBanner && (
        <EducationalBanner 
          feature="nutrition" 
          onDismiss={handleCloseBanner} 
        />
      )}
      
      <Card style={{ textAlign: 'center' }}>
        <h1 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '700' }}>📸 FoodScan Tracker</h1>
        <p style={{ color: '#64748b', margin: '0 0 16px 0' }}>
          Take a photo of your meal for instant nutrition analysis
        </p>
      </Card>

      <Card style={{ textAlign: 'center', padding: '40px' }}>
        {!scannedFood && !isScanning ? (
          <div>
            <div style={{
              width: '120px',
              height: '120px',
              backgroundColor: '#dbeafe',
              borderRadius: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto',
              border: '3px dashed #3b82f6',
              boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
            }}>
              <span style={{ fontSize: '48px' }}>📷</span>
            </div>
            
            <Button 
              onClick={simulatePhotoUpload} 
              variant="primary"
              size="lg"
              style={{ fontSize: '18px', padding: '16px 32px', fontWeight: '600' }}
            >
              Scan Your Meal
            </Button>
            
            <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '16px' }}>
              Point camera at your plate and tap to analyze
            </p>
          </div>
        ) : isScanning ? (
          <LoadingSpinner size="lg" message="Analyzing your meal..." />
        ) : (
          <div>
            <div style={{
              width: '120px',
              height: '120px',
              backgroundColor: '#bbf7d0',
              borderRadius: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto',
              boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
            }}>
              <span style={{ fontSize: '48px' }}>🥗</span>
            </div>
            
            <h3 style={{ color: '#2c3e50', marginBottom: '16px', fontWeight: '600' }}>{scannedFood.name}</h3>
            
            <div style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ color: '#64748b' }}>Protein:</span>
                <span style={{ fontWeight: '600', color: '#4ade80' }}>{scannedFood.protein}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ color: '#64748b' }}>Vegetables:</span>
                <span style={{ fontWeight: '600', color: '#4ade80' }}>{scannedFood.vegetables}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ color: '#64748b' }}>Carbohydrates:</span>
                <span style={{ fontWeight: '600' }}>{scannedFood.carbs}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <span style={{ color: '#64748b' }}>Calories:</span>
                <span style={{ fontWeight: '600' }}>{scannedFood.calories}</span>
              </div>
              
              <Card style={{ 
                backgroundColor: '#dcfce7', 
                border: '1px solid #bbf7d0',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#16a34a' }}>
                  Nutrition Score: {scannedFood.score}/100
                </div>
                <div style={{ fontSize: '14px', color: '#15803d', marginTop: '6px' }}>
                  Excellent choice! High in protein and vegetables.
                </div>
                <div style={{ marginTop: '12px' }}>
                  <ProgressBar 
                    percentage={scannedFood.score} 
                    color="#16a34a" 
                    height="6px" 
                  />
                </div>
              </Card>
            </div>
          </div>
        )}
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ color: '#2c3e50', margin: 0, fontWeight: '600' }}>📊 Today&apos;s Progress</h3>
          <Badge variant="primary">{todayScore}/100</Badge>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#64748b' }}>Daily Nutrition Score</span>
            <span style={{ fontWeight: '600' }}>{todayScore}/100</span>
          </div>
          <ProgressBar 
            percentage={todayScore} 
            color="#4361ee" 
            height="8px" 
          />
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
          <Card style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4ade80' }}>3/3</div>
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>Meals Logged</div>
          </Card>
          <Card style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>5/5</div>
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>Veggie Servings</div>
          </Card>
        </div>
      </Card>
    </div>
  );
}

export default FoodScan;
